import React, { useCallback, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import * as Bringg from '@bringg/types';
import { BringgInput, SelectableList } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { withErrorBoundary } from '@bringg-frontend/bringg-web-infra';

import VrpAutoDispatchAnalyticsContainer from 'bringg-web/features/vrp-autodispatch-analytics/vrp-auto-dispatch-analytics-container';
import { useHasFeatureFlag } from 'bringg-web/utils/feature-flags';
import { useStores } from 'bringg-web/recipes';
import { executeAction, useCrossApp } from 'bringg-web/services/cross-application/cross-application';
import { EmployeeListInMapTab } from 'bringg-web/services/cross-application/cross-application.actions';
import EmployeesFilter from './employees-filter/employees-filter';
import { EmployeeStateFilter, EmployeeAvailabilityState } from './types';
import EmployeeRow from 'bringg-web/features/dispatch-page/employee-list/employee-row';

import styles from './employee-list.module.scss';

export interface RegisterCallbacks {
	setSelectedEmployee(selectedEmployee: Bringg.User | null | undefined): void;

	setDisplayedEmployees(newDisplayed: DisplayedEmployees): void;

	setDriverListFilterByOnShift(fn: () => boolean): void;
	// Must be called after all above functions called
	finish(): void;
}

export interface DisplayedEmployees {
	all: Bringg.User[];
	filtered: Bringg.User[];
	onlineCount: number;
	onShiftAndOfflineCount: number;
	onShiftCount: number;
	offShiftCount: number;
}

const DEFAULT_DISPLAYED_EMPLOYEES: DisplayedEmployees = {
	all: [],
	filtered: [],
	onlineCount: 0,
	onShiftAndOfflineCount: 0,
	onShiftCount: 0,
	offShiftCount: 0
};

const EmployeeList = ({ cameFromEmployeeInFocus }: { cameFromEmployeeInFocus?: boolean }) => {
	const { t } = useTranslation();

	const displayVRPADAnalytics = useHasFeatureFlag('display_vrp_auto_dispatch_analytics');
	const allowSearchingEmployeesInMapTab = useHasFeatureFlag('allow_searching_employees_in_map_tab');

	const { authStore, userTypeStore, vehiclesStore } = useStores();
	const { crossAppTransport } = authStore;

	const [initFinished, setInitFinished] = useState(false);

	const [driverListFilterByOnShift, setDriverListFilterByOnShift] = useState<() => boolean>(() => () => false);

	const [selectedEmployee, setSelectedEmployee] = useState<Bringg.User | null>(null);
	const [displayedEmployees, setDisplayedEmployees] = useState<DisplayedEmployees>(DEFAULT_DISPLAYED_EMPLOYEES);

	const setDisplayedEmployeesWithFallbackToDefault = useCallback(
		(newDisplayedEmployees: DisplayedEmployees) => {
			setDisplayedEmployees(newDisplayedEmployees || DEFAULT_DISPLAYED_EMPLOYEES);
		},
		[setDisplayedEmployees]
	);

	useCrossApp(
		EmployeeListInMapTab.EMPLOYEE_LIST_EMPLOYEES_UPDATED,
		setDisplayedEmployeesWithFallbackToDefault,
		authStore.crossAppTransport
	);

	useCrossApp(
		EmployeeListInMapTab.EMPLOYEE_LIST_SELECTED_EMPLOYEE_UPDATED,
		setSelectedEmployee,
		authStore.crossAppTransport
	);

	function filterEmployees(employeeNameQuery: string) {
		executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_FILTER_CHANGE, { query: employeeNameQuery });
	}

	function changeEmployeesFilterType(filterType: EmployeeStateFilter) {
		executeAction(crossAppTransport, EmployeeListInMapTab.CHANGE_EMPLOYEE_STATE_FILTER, filterType);
	}

	function changeEmployeesAvailabilityStateFilter(filterType: EmployeeAvailabilityState | undefined) {
		executeAction(crossAppTransport, EmployeeListInMapTab.CHANGE_EMPLOYEE_AVAILABILITY_STATE_FILTER, filterType);
	}

	useEffect(() => {
		// eslint-disable-next-line prefer-const
		let interval: number;

		const initCbs: RegisterCallbacks = {
			setSelectedEmployee,
			setDisplayedEmployees(newDisplayed: DisplayedEmployees) {
				setDisplayedEmployees(newDisplayed ? newDisplayed : DEFAULT_DISPLAYED_EMPLOYEES);
			},
			setDriverListFilterByOnShift(fn: () => boolean) {
				return setDriverListFilterByOnShift(() => fn);
			},
			// Must be called after all above functions called
			finish() {
				clearInterval(interval);
				setInitFinished(true);
			}
		};

		interval = setInterval(() => {
			executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_LIST_REGISTER, initCbs);
		}, 100) as unknown as number;

		executeAction(crossAppTransport, EmployeeListInMapTab.EMPLOYEE_LIST_REGISTER, initCbs);

		return () => clearInterval(interval);
	}, [crossAppTransport]);

	useEffect(() => {
		const fetchData = async () => {
			await Promise.all([userTypeStore.fetchAll(), vehiclesStore.loadAll()]);
		};
		fetchData();
	}, []);

	const employeeStateFilterTypeCounts: Record<EmployeeStateFilter, number> = {
		[EmployeeStateFilter.ALL]: displayedEmployees.all.length,
		[EmployeeStateFilter.ONLINE]: displayedEmployees.onlineCount,
		[EmployeeStateFilter.ON_SHIFT]: displayedEmployees.onShiftCount,
		[EmployeeStateFilter.ON_SHIFT_OFFLINE]: displayedEmployees.onShiftAndOfflineCount,
		[EmployeeStateFilter.OFF_SHIFT]: displayedEmployees.offShiftCount
	};

	if (!initFinished || !userTypeStore.isFetched) {
		return <div className={styles.employeeList} id="employee_list"></div>;
	}

	return (
		<div className={styles.employeeList} id="employee_list">
			{displayVRPADAnalytics ? <VrpAutoDispatchAnalyticsContainer /> : null}

			<div className={styles.employeesHeader}>
				{allowSearchingEmployeesInMapTab ? (
					<BringgInput
						placeholder={t('GLOBAL.SEARCH')}
						className={styles.searchDrivers}
						onChange={e => filterEmployees(e.target.value)}
						prefix={<BringgIcon className="search-icon" iconName={BringgFontIcons.Search} />}
					/>
				) : null}
				<EmployeesFilter
					cameFromEmployeeInFocus={cameFromEmployeeInFocus}
					onFilterUpdate={({ employeeState, tasksState }) => {
						changeEmployeesFilterType(employeeState);
						changeEmployeesAvailabilityStateFilter(tasksState);
					}}
					employeeStateFilterTypeCounts={employeeStateFilterTypeCounts}
				/>
			</div>

			{displayedEmployees.filtered.length ? (
				<SelectableList
					itemClassName={styles.employeeListItemWrapper}
					labelKey="name"
					renderValue={employee => (
						<EmployeeRow
							employee={employee}
							selectedEmployeeId={selectedEmployee?.id}
							driverListFilterByOnShift={driverListFilterByOnShift}
						/>
					)}
					items={displayedEmployees.filtered}
					// required
					onSelect={() => undefined}
				/>
			) : null}
		</div>
	);
};

export default withErrorBoundary(observer(EmployeeList));
